import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import API from '../services/api';

const POOL_A = [
  { dark: '#618764', light: '#9CB080', name: 'Green' },
  { dark: '#760031', light: '#FF6060', name: 'Red' },
  { dark: '#D97A2B', light: '#FFA95A', name: 'Orange' },
  { dark: '#FFE97D', light: '#FFEF9F', name: 'Yellow' },
  { dark: '#093C5D', light: '#6FD1D7', name: 'Blue' },
  { dark: '#5D1C6A', light: '#831C91', name: 'Purple' },
  { dark: '#6B4A34', light: '#C9996B', name: 'Brown' },
  { dark: '#FE81D4', light: '#FAACBF', name: 'Pink' },
  { dark: '#8A7650', light: '#8E977D', name: 'Olive' },
  { dark: '#57595B', light: '#BFC6C4', name: 'Grey' },
  { dark: '#275E5B', light: '#86B8AE', name: 'Teal' },
  { dark: '#9B4F32', light: '#D99673', name: 'Clay' },
  { dark: '#5A1E2B', light: '#B86A78', name: 'Wine' },
  { dark: '#4F6043', light: '#A8B68D', name: 'Moss' },
  { dark: '#384A55', light: '#9FB5BD', name: 'Slate' },
  { dark: '#A8794B', light: '#DFC29B', name: 'Sand' },
  { dark: '#43294F', light: '#9F79A8', name: 'Plum' },
  { dark: '#C6A45D', light: '#F3E3B2', name: 'Cream' },
];

const CHAPTER_LABELS = [
  { key: 'day0', label: 'Monday — Chapter 1: The World', placeholder: "Draw the place where your story begins — a landscape, a room, a feeling of 'here'" },
  { key: 'day1', label: 'Tuesday — Chapter 2: The Character', placeholder: 'Who lives in this world? Draw the presence — person, creature, or symbol — at the center of your story' },
  { key: 'day2', label: 'Wednesday — Chapter 3: The Tension', placeholder: 'Something shifts. Draw the moment of change, conflict, or discovery that moves the story forward' },
  { key: 'day3', label: 'Thursday — Chapter 4: The Resolution', placeholder: 'How does it end? Draw the arrival, the answer, or the new beginning that closes the story' },
];

const MODE_META = {
  normal:        { label: 'Normal',        icon: '⚙️',  desc: 'Weekly Pool A pairs' },
  battle_royale: { label: 'Battle Royale', icon: '⚔️',  desc: 'Color grab — first come, first served' },
  anarchy:       { label: 'Anarchy',       icon: '🔥',  desc: 'Free for all — any color, erase anyone' },
  storyboard:    { label: 'Storyboard',    icon: '📖',  desc: '4-day collaborative story' },
};

const s = {
  page:    { maxWidth: 800, margin: '0 auto', padding: '24px 16px', fontFamily: 'Arial, sans-serif' },
  section: { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, padding: 20, marginBottom: 20 },
  heading: { fontSize: 15, fontWeight: 700, color: '#1e293b', marginBottom: 12 },
  tabRow:  { display: 'flex', gap: 8, marginBottom: 14 },
  tab:     (active) => ({
    padding: '5px 14px', borderRadius: 20, fontSize: 12, fontWeight: active ? 700 : 400, cursor: 'pointer',
    border: active ? '2px solid #3b82f6' : '1px solid #cbd5e1',
    background: active ? '#eff6ff' : '#f8fafc',
    color: active ? '#1d4ed8' : '#64748b',
  }),
  modeGrid: { display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 10, marginBottom: 14 },
  modeCard: (active) => ({
    border: active ? '2px solid #3b82f6' : '1px solid #e2e8f0',
    borderRadius: 8, padding: '10px 6px', textAlign: 'center', cursor: 'pointer',
    background: active ? '#eff6ff' : '#fff',
  }),
  btn: (variant) => ({
    padding: '7px 16px', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer', border: 'none',
    background: variant === 'danger' ? '#dc2626' : variant === 'ghost' ? '#f1f5f9' : '#2563eb',
    color: variant === 'ghost' ? '#475569' : '#fff',
  }),
  input: { width: '100%', padding: '7px 10px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' },
  label: { fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', marginBottom: 4, display: 'block' },
  msg:   (ok) => ({ padding: '8px 12px', borderRadius: 6, fontSize: 13, background: ok ? '#dcfce7' : '#fee2e2', color: ok ? '#166534' : '#991b1b', marginTop: 10 }),
};

export default function AdminCanvasPage() {
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  if (!['OFFICE_ADMIN', 'PORTAL_ADMIN'].includes(user.role)) {
    navigate('/');
    return null;
  }

  const [config, setConfig]   = useState(null);
  const [users, setUsers]     = useState([]);
  const [loading, setLoading] = useState(true);

  // Mode section state
  const [modeTab,    setModeTab]    = useState('next');
  const [selMode,    setSelMode]    = useState('normal');
  const [sbPrompts,  setSbPrompts]  = useState({ day0: '', day1: '', day2: '', day3: '' });
  const [modeSaving, setModeSaving] = useState(false);
  const [modeMsg,    setModeMsg]    = useState(null);

  // Prompt section state
  const [promptTab,    setPromptTab]    = useState('current');
  const [promptText,   setPromptText]   = useState('');
  const [promptPool,   setPromptPool]   = useState([]);
  const [showPool,     setShowPool]     = useState(false);
  const [promptSaving, setPromptSaving] = useState(false);
  const [promptMsg,    setPromptMsg]    = useState(null);

  // Color overrides section state
  const [swapTarget, setSwapTarget] = useState(null);
  const [colorMsg,   setColorMsg]   = useState(null);

  // Danger zone state
  const [resetConfirm, setResetConfirm] = useState(false);
  const [resetMsg,     setResetMsg]     = useState(null);

  const load = useCallback(async () => {
    try {
      const [cfgRes, usrRes] = await Promise.all([
        API.get('/admin/canvas/config'),
        API.get('/admin/canvas/users'),
      ]);
      setConfig(cfgRes.data);
      setUsers(usrRes.data.users);
      const nw = cfgRes.data.nextWeek;
      setSelMode(nw.mode || 'normal');
      if (nw.storyboardPrompts) setSbPrompts(nw.storyboardPrompts);
    } catch (err) {
      console.error('AdminCanvasPage load error', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!config) return;
    const week = modeTab === 'current' ? config.currentWeek : config.nextWeek;
    setSelMode(week.mode || 'normal');
    setSbPrompts(week.storyboardPrompts || { day0: '', day1: '', day2: '', day3: '' });
  }, [modeTab, config]);

  useEffect(() => {
    if (!config) return;
    const week = promptTab === 'current' ? config.currentWeek : config.nextWeek;
    setPromptText(week.prompt || '');
  }, [promptTab, config]);

  const saveMode = async () => {
    const weekStart = modeTab === 'current' ? config.currentWeek.weekStart : config.nextWeek.weekStart;
    if (selMode === 'storyboard' && (!sbPrompts.day0 || !sbPrompts.day1 || !sbPrompts.day2 || !sbPrompts.day3)) {
      setModeMsg({ ok: false, text: 'Please fill in all 4 chapter prompts.' });
      return;
    }
    setModeSaving(true);
    try {
      await API.post('/admin/canvas/config', {
        weekStart, mode: selMode,
        storyboardPrompts: selMode === 'storyboard' ? sbPrompts : undefined,
      });
      setModeMsg({ ok: true, text: 'Mode saved!' });
      await load();
    } catch {
      setModeMsg({ ok: false, text: 'Failed to save mode.' });
    } finally {
      setModeSaving(false);
    }
  };

  const savePrompt = async () => {
    const weekStart = promptTab === 'current' ? config.currentWeek.weekStart : config.nextWeek.weekStart;
    if (!promptText.trim()) { setPromptMsg({ ok: false, text: 'Prompt cannot be empty.' }); return; }
    setPromptSaving(true);
    try {
      await API.post('/admin/canvas/prompt', { weekStart, promptText });
      setPromptMsg({ ok: true, text: 'Prompt saved!' });
      await load();
    } catch {
      setPromptMsg({ ok: false, text: 'Failed to save prompt.' });
    } finally {
      setPromptSaving(false);
    }
  };

  const loadPool = async () => {
    try {
      const res = await API.get('/admin/canvas/prompts');
      setPromptPool(res.data.prompts);
      setShowPool(true);
    } catch {
      setPromptMsg({ ok: false, text: 'Failed to load prompt pool.' });
    }
  };

  const pickFromPool = async (text, weekStart) => {
    setPromptText(text);
    setShowPool(false);
    await API.post('/admin/canvas/prompt', { weekStart, promptText: text });
    setPromptMsg({ ok: true, text: 'Prompt set from pool!' });
    await load();
  };

  const swapColors = async (userId, color1, color2) => {
    try {
      await API.post(`/admin/canvas/users/${userId}/colors`, { color1, color2 });
      setColorMsg({ ok: true, text: 'Colors updated!' });
      setSwapTarget(null);
      await load();
    } catch {
      setColorMsg({ ok: false, text: 'Failed to update colors.' });
    }
  };

  const resetCanvas = async () => {
    try {
      await API.post('/admin/canvas/reset');
      setResetMsg({ ok: true, text: 'Canvas reset. All strokes and text cleared for this week.' });
      setResetConfirm(false);
    } catch {
      setResetMsg({ ok: false, text: 'Failed to reset canvas.' });
    }
  };

  const toggleVip = async (userId, grant) => {
    try {
      await API.post(`/admin/canvas/users/${userId}/vip`, { fullPalette: grant });
      setColorMsg({ ok: true, text: grant ? 'Full palette granted!' : 'Full palette revoked.' });
      await load();
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to update palette access.';
      setColorMsg({ ok: false, text: msg });
    }
  };

  if (loading) return <div style={s.page}>Loading...</div>;

  return (
    <div style={s.page}>
      <h1 style={{ fontSize: 22, fontWeight: 800, color: '#1e293b', marginBottom: 4 }}>🎨 Community Canvas Settings</h1>
      <p style={{ fontSize: 13, color: '#64748b', marginBottom: 24 }}>
        Week of {config.currentWeek.weekStart}
      </p>

      {/* ── Section 1: Mode ── */}
      <div style={s.section}>
        <div style={s.heading}>📅 Canvas Mode</div>
        <div style={s.tabRow}>
          {['current', 'next'].map(t => (
            <button key={t} style={s.tab(modeTab === t)} onClick={() => setModeTab(t)}>
              {t === 'current' ? 'This week' : 'Next week'}
            </button>
          ))}
        </div>
        <div style={s.modeGrid}>
          {Object.entries(MODE_META).map(([key, m]) => (
            <div key={key} style={s.modeCard(selMode === key)} onClick={() => setSelMode(key)}>
              <div style={{ fontSize: 22 }}>{m.icon}</div>
              <div style={{ fontSize: 12, fontWeight: 600, color: selMode === key ? '#1d4ed8' : '#475569' }}>{m.label}</div>
              <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 2 }}>{m.desc}</div>
            </div>
          ))}
        </div>

        {selMode === 'storyboard' && (
          <div style={{ border: '1px solid #bfdbfe', borderRadius: 8, background: '#f0f9ff', padding: 14, marginBottom: 12 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#1e40af', marginBottom: 10 }}>📖 Storyboard Prompts</div>
            {CHAPTER_LABELS.map(({ key, label, placeholder }) => (
              <div key={key} style={{ marginBottom: 10 }}>
                <label style={s.label}>{label}</label>
                <input
                  style={s.input}
                  value={sbPrompts[key] || ''}
                  placeholder={placeholder}
                  onChange={e => setSbPrompts(p => ({ ...p, [key]: e.target.value }))}
                />
              </div>
            ))}
          </div>
        )}

        <button style={s.btn('primary')} onClick={saveMode} disabled={modeSaving}>
          {modeSaving ? 'Saving…' : `Save Mode for ${modeTab === 'current' ? 'This Week' : 'Next Week'}`}
        </button>
        {modeMsg && <div style={s.msg(modeMsg.ok)}>{modeMsg.text}</div>}
      </div>

      {/* ── Section 2: Prompt ── */}
      <div style={s.section}>
        <div style={s.heading}>💬 Weekly Prompt</div>
        <div style={s.tabRow}>
          {['current', 'next'].map(t => (
            <button key={t} style={s.tab(promptTab === t)} onClick={() => setPromptTab(t)}>
              {t === 'current' ? 'This week' : 'Next week'}
            </button>
          ))}
        </div>
        {(promptTab === 'current' ? config.currentWeek : config.nextWeek).prompt && (
          <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 6, padding: '8px 12px', fontSize: 13, color: '#475569', marginBottom: 10 }}>
            Current: <em>"{(promptTab === 'current' ? config.currentWeek : config.nextWeek).prompt}"</em>
          </div>
        )}
        <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
          <input style={{ ...s.input, flex: 1 }} value={promptText} placeholder="Type a custom prompt…"
            onChange={e => setPromptText(e.target.value)} />
          <button style={s.btn('primary')} onClick={savePrompt} disabled={promptSaving}>
            {promptSaving ? 'Saving…' : 'Set'}
          </button>
        </div>
        <button style={{ ...s.btn('ghost'), fontSize: 12 }} onClick={loadPool}>
          Pick from pool ({config.poolCount} unused)
        </button>
        {promptMsg && <div style={s.msg(promptMsg.ok)}>{promptMsg.text}</div>}
        {showPool && (
          <div style={{ marginTop: 12, border: '1px solid #e2e8f0', borderRadius: 8, maxHeight: 200, overflowY: 'auto' }}>
            {promptPool.length === 0
              ? <div style={{ padding: 12, fontSize: 13, color: '#94a3b8' }}>No unused prompts.</div>
              : promptPool.map(p => (
                <div key={p.id} style={{ padding: '8px 12px', borderBottom: '1px solid #f1f5f9', cursor: 'pointer', fontSize: 13 }}
                  onClick={() => pickFromPool(p.prompt_text, (promptTab === 'current' ? config.currentWeek : config.nextWeek).weekStart)}>
                  {p.prompt_text}
                </div>
              ))
            }
          </div>
        )}
      </div>

      {/* ── Section 3: User Color Overrides ── */}
      <div style={s.section}>
        <div style={s.heading}>
          🎨 User Color Overrides
          <span style={{ fontSize: 11, fontWeight: 400, color: '#94a3b8', marginLeft: 8 }}>(Normal mode only)</span>
        </div>
        {config.currentWeek.mode !== 'normal' && (
          <div style={{ fontSize: 13, color: '#94a3b8', fontStyle: 'italic', marginBottom: 8 }}>
            Color overrides apply in Normal mode. Current week is {MODE_META[config.currentWeek.mode]?.label}.
          </div>
        )}
        {users.map(u => (
          <div key={u.id} style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', background: '#f8fafc', borderRadius: 6, border: '1px solid #e2e8f0', marginBottom: 8 }}>
            <div style={{ width: 32, height: 32, borderRadius: '50%', background: u.color1 || '#e2e8f0', border: '2px solid #fff', boxShadow: '0 0 0 1px #e2e8f0', flexShrink: 0 }} />
            <div style={{ flex: 1 }}>
              <span style={{ fontSize: 13, fontWeight: 600 }}>{u.first_name} {u.last_name}</span>
              {u.full_palette && <span style={{ marginLeft: 8, background: '#7c3aed', color: '#fff', fontSize: 10, padding: '1px 6px', borderRadius: 10 }}>VIP</span>}
            </div>
            <div style={{ display: 'flex', gap: 4 }}>
              {u.color1 && <div style={{ width: 18, height: 18, borderRadius: 3, background: u.color1, border: '1px solid #e2e8f0' }} title={u.color1} />}
              {u.color2 && <div style={{ width: 18, height: 18, borderRadius: 3, background: u.color2, border: '1px solid #e2e8f0' }} title={u.color2} />}
            </div>
            <button style={{ ...s.btn('ghost'), fontSize: 11, padding: '3px 10px' }} onClick={() => setSwapTarget(swapTarget === u.id ? null : u.id)}>
              Swap pair
            </button>
            {u.color1 ? (
              <button style={{ fontSize: 11, padding: '3px 10px', border: `1px solid ${u.full_palette ? '#dc2626' : '#8b5cf6'}`, borderRadius: 6, background: u.full_palette ? '#fef2f2' : '#f5f3ff', color: u.full_palette ? '#dc2626' : '#7c3aed', cursor: 'pointer' }}
                onClick={() => toggleVip(u.id, !u.full_palette)}>
                {u.full_palette ? 'Revoke VIP' : 'Full palette'}
              </button>
            ) : (
              <span style={{ fontSize: 11, color: '#94a3b8' }}>Hasn't opened canvas yet</span>
            )}

            {swapTarget === u.id && (
              <div style={{ position: 'absolute', top: '100%', right: 0, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, padding: 12, boxShadow: '0 4px 16px rgba(0,0,0,0.12)', zIndex: 100, display: 'grid', gridTemplateColumns: 'repeat(5,1fr)', gap: 6, marginTop: 4 }}>
                {POOL_A.map(pair => (
                  <div key={pair.name} title={pair.name} style={{ cursor: 'pointer', display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'center' }}
                    onClick={() => swapColors(u.id, pair.dark, pair.light)}>
                    <div style={{ width: 22, height: 22, borderRadius: 4, background: pair.dark }} />
                    <div style={{ width: 22, height: 10, borderRadius: 3, background: pair.light }} />
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
        {colorMsg && <div style={s.msg(colorMsg.ok)}>{colorMsg.text}</div>}
      </div>

      {/* ── Section 4: Danger Zone ── */}
      <div style={{ ...s.section, border: '1px solid #fecaca' }}>
        <div style={{ ...s.heading, color: '#dc2626' }}>⚠️ Danger Zone</div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6, padding: '10px 14px' }}>
          <div>
            <div style={{ fontWeight: 600, fontSize: 13, color: '#991b1b' }}>Reset Current Canvas</div>
            <div style={{ fontSize: 12, color: '#dc2626', marginTop: 2 }}>Clears all strokes and text for this week. Cannot be undone.</div>
          </div>
          {!resetConfirm
            ? <button style={{ ...s.btn('ghost'), border: '1px solid #dc2626', color: '#dc2626', background: '#fff' }} onClick={() => setResetConfirm(true)}>Reset Canvas</button>
            : <div style={{ display: 'flex', gap: 8 }}>
                <button style={s.btn('danger')} onClick={resetCanvas}>Confirm Reset</button>
                <button style={s.btn('ghost')} onClick={() => setResetConfirm(false)}>Cancel</button>
              </div>
          }
        </div>
        {resetMsg && <div style={s.msg(resetMsg.ok)}>{resetMsg.text}</div>}
      </div>
    </div>
  );
}
